import { useCallback } from 'react'

import usePersistedState from '@hooks/usePersistedState.ts'

export interface HistoryEntry {
  term: string
  lastUsed: number
  uses: number
  important?: boolean
}

/**
 * Manages a history of search terms with usage tracking and importance flagging.
 * - Stores history in local storage under the key 'product-finder-history'.
 * - Each entry tracks the term, last used timestamp, usage count, and importance.
 * - Important entries are those used more than a defined threshold (10 times).
 * - History is limited to the 10 most recent entries, prioritizing important ones.
 */

// maybe we will move this over to zustand or comparable later

function useHistory() {
  const [history, setHistory] = usePersistedState<HistoryEntry[]>({ key: 'product-finder:history', initialState: [] })

  const IMPORTANT_THRESHOLD = 10

  const addOrUpdate = useCallback((term: string) => {
    setHistory((old) => {
      const now = Date.now()

      const existing = old.find((entry) => entry.term === term)
      const filtered = old.filter((entry) => entry.term !== term)

      const newEntry: HistoryEntry = existing
        ? {
          ...existing,
          lastUsed: now,
          uses: existing.uses + 1,
          important: existing.uses + 1 >= IMPORTANT_THRESHOLD,
        }
        : {
          term,
          lastUsed: now,
          uses: 1,
          important: false,
        }

      return [newEntry, ...filtered]
        .sort((a, b) => {
          // important entries always come first
          if (a.important && !b.important) return -1
          if (!a.important && b.important) return 1

          // otherwise sort by recency
          return b.lastUsed - a.lastUsed
        })
        .slice(0, 10)
    })
  }, [setHistory])

  const remove = useCallback((term: string) => {
    setHistory((old) => old.filter((entry) => entry.term !== term))
  }, [setHistory])

  return [history, addOrUpdate, remove] as const
}

export default useHistory
